import { Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Sidebar from "@/components/layout/Sidebar";
import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { PanelLeftClose, PanelLeftOpen, LogOut } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/store";
import { logout } from "@/store/authSlice";

export default function SuperAdminLayout() {
  const [open, setOpen] = useState(true);
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    dispatch(logout());
  };

  const portalName = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN" 
    ? "Operations Center" 
    : user?.clientName || "Client Portal";

  const userDisplayName = user?.firstName ? `${user.firstName} ${user.lastName || ""}` : (user?.role || "User");

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar open={open} />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b flex items-center justify-between px-6 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen((prev) => !prev)}
              className="h-9 w-9"
            >
              {open ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
            </Button>
            <div className="flex flex-col">
              <span className="text-sm font-semibold leading-none">{userDisplayName}</span>
              <span className="text-xs text-muted-foreground">{portalName}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <ModeToggle />
            <Button variant="ghost" size="sm" className="hidden sm:flex" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
